/**
 * EventPanel 工具：扩展事件筛选标题
 * 在 generateEventFilterTitle 基础上追加「事件发生」与「事件发生次数」文案，不修改原方法。
 */
import { EventFilterNumberOptions, EventFilterTextOptions } from '@/pages/datalab/components/SortableDrag/configration';
import { getName } from '@home/utils/common';
import type { TEventParameterValue } from '../../types';
import type { IGenerateEventTitleParams } from './type';

export const CUSTOM_VALUE = 'custom';

/** numeric 类型无 children 时在 markets 中的 value 后缀，与 EventParameterPanel 一致 */
export const NUMERIC_CHILD_SUFFIX = '@__@numeric';

/** 事件参数选项的通用结构（供 buildEventParameterDisplayParts 使用） */
export interface IEventParameterOptionLike {
  value: string;
  type?: string;
  textEn?: string;
  textCN?: string;
  children?: Array<{ value: string; textEn?: string; textCN?: string; }>;
}

/**
 * 根据 market 值解析所属一级 key：numeric 为去掉后缀的 value，否则从 options 中查所属 option.value
 */
function getFirstLevelKey(
  marketValue: string,
  eventParameterOptions: IEventParameterOptionLike[]
): string | null {
  if (marketValue.endsWith(NUMERIC_CHILD_SUFFIX)) {
    return marketValue.slice(0, -NUMERIC_CHILD_SUFFIX.length);
  }
  const option = eventParameterOptions.find((o) =>
    o.children?.some((c) => c.value === marketValue)
  );
  return option?.value ?? null;
}

/**
 * 根据 eventParam 与 options 生成事件参数展示片段（markets + 已勾选 numeric），供标题与按钮复用。
 * 仅一级选项按用户勾选顺序展示（eventParam.markets 中首次出现该一级的先后）；同一一级下的二级按配置顺序展示。
 */
export function buildEventParameterDisplayParts(params: {
  eventParam: TEventParameterValue | null | undefined;
  eventParameterOptions: IEventParameterOptionLike[];
  isEn: boolean;
}): string[] {
  const { eventParam, eventParameterOptions, isEn } = params;
  const markets = eventParam?.markets ?? [];
  const numeric = eventParam?.numeric ?? {};
  const hasMarkets = markets.length > 0;
  const hasNumeric = Object.keys(numeric).length > 0;

  if (!hasMarkets && !hasNumeric) return [];

  const firstLevelOrder: Record<string, number> = {};
  const regionChildValues: Record<string, string[]> = {};

  markets.forEach((marketValue: string, index: number) => {
    const key = getFirstLevelKey(marketValue, eventParameterOptions);
    if (key === null) return;

    if (firstLevelOrder[key] === undefined) {
      firstLevelOrder[key] = index;
    }
    if (!marketValue.endsWith(NUMERIC_CHILD_SUFFIX)) {
      if (!regionChildValues[key]) regionChildValues[key] = [];
      if (!regionChildValues[key].includes(marketValue)) {
        regionChildValues[key].push(marketValue);
      }
    }
  });

  const sortedKeys = Object.keys(firstLevelOrder).sort(
    (a, b) => firstLevelOrder[a] - firstLevelOrder[b]
  );

  const optionByValue = new Map(
    eventParameterOptions.map((o) => [o.value, o])
  );

  const paramParts: string[] = [];

  sortedKeys.forEach((key) => {
    const option = optionByValue.get(key);
    const hasChildren = (option?.children?.length ?? 0) > 0;

    if (hasChildren && regionChildValues[key]?.length && option?.children) {
      const regionText =
        getName(isEn, option.textEn, option.textCN) || option.value;
      const selectedSet = new Set(regionChildValues[key]);
      const childTexts = option.children
        .filter((c) => selectedSet.has(c.value))
        .map((c) => getName(isEn, c.textEn, c.textCN) || c.value);
      paramParts.push(`${regionText}=${childTexts.join('&')}`);
      return;
    }

    const numericMarketValue = `${key}${NUMERIC_CHILD_SUFFIX}`;
    if (!markets.includes(numericMarketValue)) return;

    const cond = numeric[key];
    if (!cond?.operator && !(cond?.value?.length)) return;

    const paramName = option
      ? getName(isEn, option.textEn, option.textCN) || key
      : key;
    const numericDisplay = buildNumericDisplay(
      cond.operator ?? '',
      cond.value ?? [],
      isEn,
      option?.type ?? ''
    );
    const isBetween = cond.operator === 'between';
    const isText = option?.type === 'text';
    paramParts.push(`${paramName}${isText ? ':' : ''}${isBetween ? '=' : ''}${numericDisplay}`);
  });

  return paramParts;
}

/** between 时仅当两个都有数值才用 - 分割，只有一个有数值时不分割（供 buildNumericDisplay / 面板复用） */
export function formatNumericValueStr(operator: string, operatorValue: string[]): string {
  const filled = operatorValue.map((v) => String(v ?? '').trim()).filter(Boolean);
  if (operator === 'between') {
    return filled.length >= 2 ? filled.join('-') : filled[0] ?? '';
  }
  return filled.join('');
}

/** 根据操作符与数值生成 numeric 参数的中英文展示文案 */
export function buildNumericDisplay(
  operator: string,
  operatorValue: string[],
  isEn: boolean,
  type: string
): string {
  const isNumeric = type === 'numeric';
  const options = isNumeric ? EventFilterNumberOptions : EventFilterTextOptions;
  const opt = options?.find((o) => o.value === operator);
  const opText = opt ? getName(isEn, opt.text_en, opt.text) : operator;
  const isBetween = operator === 'between';
  const valueStr = formatNumericValueStr(operator, operatorValue);
  return valueStr ? `${isBetween ? '' : opText}${valueStr}` : `${opText}`;
}

/** 扩展事件筛选标题：在 generateEventFilterTitle 基础上追加「事件发生」与「事件发生次数」文案 */
export function generateEventTitle(
  params: IGenerateEventTitleParams
): string {
  const {
    value,
    eventNameOptions,
    eventParameterOptions,
    displayConfig,
    eventOccurOptions,
    isEn,
    hoursSinceRegisterLabel,
    eventOccurLabel,
    eventOccurCountLabel,
    placeholder,
  } = params;
  const parts: string[] = [];

  const hours = value.hoursSinceRegister === CUSTOM_VALUE
    ? value.hoursSinceRegisterCustom
    : value.hoursSinceRegister;

  if (hours) {
    parts.push(`${hoursSinceRegisterLabel}${hours}h`);
  }

  if (value.eventName) {
    const option = eventNameOptions?.find(o => o.value === value.eventName);
    const optionWithI18n = option as (typeof option) & { textEn?: string; textCN?: string; };
    const text = optionWithI18n
      ? getName(
        isEn,
        optionWithI18n.textEn ?? optionWithI18n.text,
        optionWithI18n.textCN ?? optionWithI18n.text
      )
      : value.eventName;
    parts.push(text);
  }

  const paramParts = buildEventParameterDisplayParts({
    eventParam: value.eventParameter,
    eventParameterOptions: eventParameterOptions ?? [],
    isEn,
  });
  if (paramParts.length > 0) {
    parts.push(paramParts.join(', '));
  }

  if (displayConfig?.eventOccurDisplay === 'aggregation' && value.aggregationType) {
    const opt = eventOccurOptions.find((o) => o.value === value.aggregationType);
    let optionText = '';
    if (opt) {
      optionText = getName(isEn, opt?.textEn, opt?.textCn);
    }
    if (optionText) {
      const text = eventOccurLabel ? `${eventOccurLabel}: ${optionText}` : optionText;
      parts.push(text);
    }
  }
  if (
    displayConfig?.eventOccurDisplay === 'count' &&
    value.occurCountOperator &&
    (value.occurCountValue?.length ?? 0) > 0
  ) {
    const opOpt = EventFilterNumberOptions.find((o) => o.value === value.occurCountOperator);
    let opSymbol = '';
    if (opOpt) {
      opSymbol = getName(isEn, opOpt.text_en, opOpt.text);
    }
    const opText = opSymbol !== '' ? opSymbol : value.occurCountOperator;
    const vals = value.occurCountValue ?? [];
    const v0 = String(vals[0] ?? '').trim();
    const v1 = String(vals[1] ?? '').trim();
    let valueText = v0;
    if (value.occurCountOperator === 'between') {
      valueText = v0 && v1 ? `${v0}-${v1}` : '';
    }
    if (valueText) {
      const countText = `${opText} ${valueText}`;
      const text = eventOccurCountLabel ? `${eventOccurCountLabel}: ${countText}` : countText;
      parts.push(text);
    }
  }

  if (parts?.length === 0) {
    return placeholder ?? '';
  }

  return parts?.join('; ');
}
